import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  ButtonWithAction,
  CloseIcon,
  Cluster,
  Detail,
  Stack,
  Text,
  Title,
  constants,
  util
} from "@thecb/components";
import AlertBar from "/components/alert-bar";
import Slider, { SliderPosition } from "/components/slider";
import { SliderCloseButton } from "/components/slider/Slider";
import SliderPanel from "/components/slider-panel";
import IconsModule from "/components/obligation/modules/IconModule";
import TurnstileWidget from "/components/turnstile-widget";
import InlinePaymentCaptureSection from "./InlinePaymentCaptureSection";
import {
  CC_METHOD,
  TURNSTILE
} from "../../../../../../constants/data_constants";

const {
  WHITE,
  CHARADE_GREY,
  GHOST_GREY,
  ROYAL_BLUE_VIVID,
  STORM_GREY
} = constants.colors;
const { FONT_WEIGHT_SEMIBOLD } = constants.fontWeights;
const { displayCurrency } = util.general;

const SliderHeader = ({ titleId, onClose, isMobile }) => (
  <Box
    background={CHARADE_GREY}
    padding="1.5rem"
    extraStyles={`
      position: sticky;
      top: 0;
      z-index: 10;
    `}
  >
    <Cluster
      justify="space-between"
      align="center"
      extraStyles="overflow: visible;"
    >
      <Title id={titleId} color={WHITE} variant="small" as="h2">
        Set Up Autopay
      </Title>
      {isMobile && (
        <ButtonWithAction
          variant="smallGhost"
          contentOverride
          action={onClose}
          dataQa="close-payment-capture-slider"
          aria-label="Close"
          extraStyles={`
            padding: 0.15rem;
            min-width: 1.5rem;
            min-height: auto;
            margin: 0;
            svg { height: 12px; width: 12px; }
          `}
        >
          <CloseIcon
            iconFill={WHITE}
            iconWidth="12px"
            iconHeight="12px"
            aria-hidden="true"
          />
        </ButtonWithAction>
      )}
    </Cluster>
  </Box>
);

const PaymentCaptureSlider = ({
  isOpen,
  isMobile,
  onClose,
  onAddCreditCard,
  onAddACH,
  allowedPaymentInstruments,
  addCreditCardForm,
  addCreditCardFormActions,
  addAchForm,
  addAchFormActions,
  captchaFormActions,
  siteKey,
  verifyURL,
  showErrors = false,
  detailedObligation,
  addCreditCardAlertBar,
  addCreditCardAlertBarActions,
  addAchAlertBar,
  addAchAlertBarActions,
  isAddingPayment = false
}) => {
  const firstObligation = detailedObligation?.obligations?.[0];
  const obligationName = detailedObligation?.name ?? "";
  const totalAmountDue =
    detailedObligation?.obligations?.reduce(
      (acc, curr) => acc + (curr.amountDue ?? 0),
      0
    ) ?? 0;

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [activePaymentType, setActivePaymentType] = useState("");
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const captchaRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setShowPaymentForm(false);
      setActivePaymentType("");
      setIsCaptchaVerified(false);
      captchaRef.current?.reset();
    } else {
      // Clear forms when slider opens to prevent persisted data
      addCreditCardFormActions?.actions.form.clear();
      addAchFormActions?.actions.form.clear();
      captchaFormActions?.actions.form.clear();
    }
  }, [isOpen]);

  const handleCaptchaExpired = () => {
    captchaFormActions?.actions.fields.captchaToken.set("");
    setIsCaptchaVerified(false);
    captchaRef.current?.reset();
  };

  const handleAddPayment =
    activePaymentType === CC_METHOD ? onAddCreditCard : onAddACH;

  return (
    <Slider
      isOpen={isOpen}
      isMobile={isMobile}
      position={SliderPosition.DOCUMENT_TOP}
      closeSlider={onClose}
      ariaLabelledBy="payment-capture-slider-title"
    >
      {!isMobile && (
        <SliderCloseButton
          closeAction={onClose}
          ariaLabel="Close Payment Capture Slider"
        />
      )}

      <SliderPanel
        status={showPaymentForm ? "offScreen" : "onScreen"}
        sliderKey="payment-capture-start-panel"
      >
        <Box
          padding="0"
          minWidth="100%"
          minHeight="100%"
          extraStyles="display: flex; flex-direction: column; height: 100%;"
        >
          <SliderHeader
            titleId="payment-capture-slider-title"
            onClose={onClose}
            isMobile={isMobile}
          />
          <Box
            background={WHITE}
            padding="0 1.5rem"
            extraStyles="overflow-y: auto; flex: 1; min-height: 0;"
          >
            <Box padding="1.5rem 0 0">
              <Stack childGap="1.5rem">
                <Stack childGap="0.5rem">
                  <Title variant="small" as="h3">
                    Payment Method Required for Autopay
                  </Title>
                  <Text variant="p">
                    You don&apos;t have a payment method on file. To set up
                    autopay for this account you need to add a payment method.
                  </Text>
                </Stack>
                {firstObligation && (
                  <Box
                    padding="1rem"
                    borderSize="1px"
                    borderColor={GHOST_GREY}
                    extraStyles="border-radius: 4px;"
                  >
                    <Cluster
                      justify="space-between"
                      align="center"
                      childGap="4px"
                      nowrap
                    >
                      <Box padding="0">
                        <Cluster justify="flex-start" align="center">
                          {detailedObligation?.config?.iconDefault && (
                            <IconsModule
                              icon={detailedObligation.config.iconDefault}
                              iconColor={ROYAL_BLUE_VIVID}
                            />
                          )}
                          <Box padding="0">
                            <Stack childGap="0">
                              <Title
                                variant="small"
                                weight={FONT_WEIGHT_SEMIBOLD}
                                as="h3"
                              >
                                {obligationName}
                              </Title>
                              {firstObligation.subDescription && (
                                <Detail
                                  variant="small"
                                  color={STORM_GREY}
                                  as="p"
                                >
                                  {firstObligation.subDescription}
                                </Detail>
                              )}
                            </Stack>
                          </Box>
                        </Cluster>
                      </Box>
                      <Text variant="p" weight={FONT_WEIGHT_SEMIBOLD}>
                        {displayCurrency(totalAmountDue)}
                      </Text>
                    </Cluster>
                  </Box>
                )}
              </Stack>
            </Box>
            <Box padding="1.5rem 0 1.5rem">
              <ButtonWithAction
                text="Get Started"
                variant="primary"
                action={() => setShowPaymentForm(true)}
                dataQa="Get Started"
                borderRadius="10px"
                extraStyles="width: 100%; margin: 0;"
              />
            </Box>
          </Box>
        </Box>
      </SliderPanel>

      <SliderPanel
        status={showPaymentForm ? "onScreen" : "next"}
        sliderKey="payment-capture-form-panel"
      >
        <Box
          padding="0"
          minWidth="100%"
          minHeight="100%"
          extraStyles="display: flex; flex-direction: column; height: 100%;"
        >
          <SliderHeader
            titleId="payment-capture-slider-title"
            onClose={onClose}
            isMobile={isMobile}
          />
          <Box
            background={WHITE}
            padding="0 1.5rem"
            extraStyles="overflow-y: auto; flex: 1; min-height: 0;"
          >
            <Box padding="1.5rem 0 0">
              <Stack childGap="1.5rem">
                <Stack childGap="0.5rem">
                  <Title variant="small" as="h3">
                    Add New Payment Method
                  </Title>
                  <Text variant="p">
                    Enter the details of the payment method you would like to
                    use going forward.
                  </Text>
                </Stack>
                {(addCreditCardAlertBar?.alerts?.length > 0 ||
                  addAchAlertBar?.alerts?.length > 0) && (
                  <Box padding="0">
                    {addCreditCardAlertBar?.alerts?.length > 0 && (
                      <AlertBar
                        {...addCreditCardAlertBar}
                        {...addCreditCardAlertBarActions}
                      />
                    )}
                    {addAchAlertBar?.alerts?.length > 0 && (
                      <AlertBar
                        {...addAchAlertBar}
                        {...addAchAlertBarActions}
                      />
                    )}
                  </Box>
                )}
                <InlinePaymentCaptureSection
                  allowedPaymentInstruments={allowedPaymentInstruments}
                  addCreditCardForm={addCreditCardForm}
                  addCreditCardFormActions={addCreditCardFormActions}
                  addAchForm={addAchForm}
                  addAchFormActions={addAchFormActions}
                  showErrors={showErrors}
                  selectedPaymentType={activePaymentType}
                  onPaymentTypeChange={setActivePaymentType}
                />
                {activePaymentType && siteKey && (
                  <TurnstileWidget
                    ref={captchaRef}
                    verifyURL={verifyURL}
                    siteKey={siteKey}
                    onSuccess={token => {
                      captchaFormActions?.actions.fields.captchaToken.set(
                        token
                      );
                      captchaFormActions?.actions.fields.captchaProvider.set(
                        TURNSTILE
                      );
                      setIsCaptchaVerified(true);
                    }}
                    onError={() => {
                      captchaFormActions?.actions.fields.captchaToken.set("");
                      setIsCaptchaVerified(false);
                    }}
                    onExpired={handleCaptchaExpired}
                    padding="0 0 0.5rem"
                  />
                )}
              </Stack>
            </Box>
            <Box padding="1.5rem 0 1.5rem">
              <ButtonWithAction
                text="Add Payment Method"
                variant="primary"
                action={handleAddPayment}
                dataQa="Add Payment Method"
                borderRadius="10px"
                extraStyles="width: 100%; margin: 0;"
                disabled={!!siteKey && !isCaptchaVerified}
                isLoading={isAddingPayment}
              />
            </Box>
          </Box>
        </Box>
      </SliderPanel>
    </Slider>
  );
};

export default PaymentCaptureSlider;
